import { FicheDePrep } from '@teetsh/app/src/types/sequences';
import { useField } from 'react-final-form';

import { fdpPhasesToHtml } from 'containers/CahierJournal/EventModal/ModalBody/Classic/LinkedFichesDePreps/fdpPhasesToHtml';
import { uniqBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { getDurationInHoursAndMinutes } from '@teetsh/app/src/utils/time/getDurationInHoursAndMinutes';
import colors from 'colors';
import addMinutes from 'utils/time/addMinutes';
import { OldCustomField } from 'types/oldCustomField';
import { useUpdateEventCustomFieldDefinition } from 'queries/events/customfields/useUpdateEventCustomFieldDefinition';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { getTargetCustomFieldsFromSource } from './getCJSeanceCustomFieldsFromFdpForNewCF';
import { useEventCustomFieldDefinitions } from 'queries/events/customfields/useEventCustomFieldDefinitions';
import { useSequenceCustomFieldDefinitions } from 'queries/sequences/customfields/useSequenceCustomFieldDefinitions';

export function useSelectFicheDePrep() {
  const { data: customFieldDefinitions = [] } =
    useSequenceCustomFieldDefinitions();
  const filteredCustomFieldDefinitions = customFieldDefinitions.filter(cf =>
    cf.targets?.includes('fdpPhases'),
  );
  const boField = useField('bo');
  const titleField = useField('title');
  const startField = useField('start');
  const endField = useField('end');
  const materialsField = useField('materials');
  const customFields = useField<OldCustomField[]>('customFields');
  const attachmentsField = useField('attachments');
  const flowField = useField('flow');
  const { t } = useTranslation(['cahierJournal', 'common']);
  const flow = flowField.input.value;
  const bo = boField.input.value;
  const materials = materialsField.input.value;
  const attachments = attachmentsField.input.value;

  const { school, schoolyear } = useSchoolSchoolyear();
  const { data: eventCustomFieldDefinitions = [] } =
    useEventCustomFieldDefinitions({
      schoolId: school.id,
      schoolyearId: schoolyear.id,
    });
  const { data: sequenceCustomFieldDefinitions = [] } =
    useSequenceCustomFieldDefinitions();
  const { mutateAsync: onUpdateDefinition } =
    useUpdateEventCustomFieldDefinition({
      schoolId: school.id,
      schoolyearId: schoolyear.id,
    });

  return (ficheDePrep: FicheDePrep, rawSequenceTitle: string) => {
    let newBo = { ...bo };
    if (bo.cycle === undefined) {
      newBo = {
        ...newBo,
        cycle: ficheDePrep.bo.cycle,
      };
    }
    if ((ficheDePrep.bo.matiere || []).length > 0) {
      newBo = {
        ...newBo,
        matiere: uniqBy([...newBo.matiere, ...ficheDePrep.bo.matiere], 'value'),
      };
    }
    if ((ficheDePrep.bo.competence || []).length > 0) {
      newBo = {
        ...newBo,
        competence: uniqBy(
          [...newBo.competence, ...ficheDePrep.bo.competence],
          'value',
        ),
      };
    }
    if ((ficheDePrep.bo.goals || []).length > 0) {
      newBo = {
        ...newBo,
        goals: uniqBy([...newBo.goals, ...ficheDePrep.bo.goals], 'value'),
      };
    }
    if ((ficheDePrep.bo.domaine || []).length > 0) {
      newBo = {
        ...newBo,
        domaine: uniqBy([...newBo.domaine, ...ficheDePrep.bo.domaine], 'value'),
      };
    }
    const dureeInMinute = ficheDePrep.phases
      .map(p => p.dureeInMinute)
      .reduce((acc, duree) => acc + duree, 0);

    const fillSeanceTitleFromFdp = titleField.input.value === '';
    if (fillSeanceTitleFromFdp) {
      // populate title and end if the session looks virgin (=title is empty)
      titleField.input.onChange(ficheDePrep.title);
      if (dureeInMinute > 0) {
        endField.input.onChange(
          addMinutes(startField.input.value, dureeInMinute),
        );
      }
    }

    boField.input.onChange(newBo);
    materialsField.input.onChange(
      uniqBy([...(materials || []), ...(ficheDePrep.materials || [])], 'value'),
    );
    const { updatedCustomFields, missingCustomFieldOptions } =
      getTargetCustomFieldsFromSource(
        ficheDePrep.customFields || [],
        sequenceCustomFieldDefinitions,
        customFields.input.value,
        eventCustomFieldDefinitions,
      );
    customFields.input.onChange(updatedCustomFields);
    Object.entries(missingCustomFieldOptions).forEach(([key, newOptions]) => {
      const def = eventCustomFieldDefinitions.find(v => v.id === key);
      if (def) {
        onUpdateDefinition({
          ...def,
          options: [...def.options, ...newOptions],
        });
      }
    });

    if (attachments === undefined || attachments.length === 0) {
      attachmentsField.input.onChange(ficheDePrep.attachments);
    }

    const sequenceTitle = fillSeanceTitleFromFdp
      ? ''
      : `<h4 style="font-size: 1.25rem">${rawSequenceTitle}</h4>`;
    const dureeHtml = dureeInMinute
      ? `<strong style="font-size: 0.9rem;color: ${
          colors.gray['400']
        };">${getDurationInHoursAndMinutes(dureeInMinute)}</strong>`
      : '';
    const ficheDePrepTitle = fillSeanceTitleFromFdp
      ? ''
      : `<h4 style="font-size: 1rem">${ficheDePrep.title} ${dureeHtml}</h4>`;
    const phasesHtml = fdpPhasesToHtml(
      ficheDePrep.phases || [],
      filteredCustomFieldDefinitions,
      t,
    );

    flowField.input.onChange(
      `${flow}${sequenceTitle}${ficheDePrepTitle}${phasesHtml}`,
    );
  };
}
